/**
 * State machine for the global error banner. Pure — the component just holds a
 * BannerState in useState and feeds events through these transitions.
 *
 * Rules: one banner at a time (repeats of the same message bump a counter, a
 * different error replaces it), and once the user dismisses or reports a
 * message it stays muted for the rest of the session so the banner never nags.
 */

import type { NormalizedError } from './errorReport'

export interface BannerState {
  visible: boolean
  report: NormalizedError | null
  /** Occurrences of the current message since it was first shown. */
  count: number
  /** Messages the user has dismissed or reported this session. */
  muted: string[]
}

export const initialBannerState: BannerState = {
  visible: false,
  report: null,
  count: 0,
  muted: [],
}

export function bannerErrorCaught(state: BannerState, report: NormalizedError): BannerState {
  if (state.muted.includes(report.message)) return state
  if (state.visible && state.report?.message === report.message) {
    return { ...state, count: state.count + 1 }
  }
  return { ...state, visible: true, report, count: 1 }
}

function muteCurrent(state: BannerState): BannerState {
  if (!state.report) return state
  return {
    ...state,
    visible: false,
    muted: state.muted.includes(state.report.message)
      ? state.muted
      : [...state.muted, state.report.message],
  }
}

export function bannerDismissed(state: BannerState): BannerState {
  return muteCurrent(state)
}

export function bannerReportOpened(state: BannerState): BannerState {
  return muteCurrent(state)
}
